import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { Column } from '@repo/ui/data-table'
import { DeleteIcon, GoalIcon, LocationThreeIcon, Mission2Icon, PlusIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import { isSameDay } from 'date-fns'
import { useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { cancelMission } from '../../../api/missions/cancel-mission'
import { createMission } from '../../../api/missions/create-mission'
import CountryFlag from '../../../components/elements/country-flag'
import FilterDrawer from '../../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import { countFilters } from '../../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../../components/elements/nav-bar'
import TransportPicto from '../../../components/elements/transport-picto'
import MissionForm from '../../../components/pages/mission/forms/mission-form'
import MissionDetails from '../../../components/pages/mission/mission-details'
import MissionRowDisplay from '../../../components/pages/trip-steps/mission-row-display'
import { TripStepsFilter } from '../../../components/pages/trip-steps/trip-steps-filter'
import { useFilters } from '../../../lib/hooks/use-filters'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { tripStepTypeMapping, tripTypeMapping } from '../../../lib/types/enums/trip/trip-type.enum'
import { TripBookingTypes } from '../../../lib/types/models/trip-booking/trip-booking'
import { TripStepTypes } from '../../../lib/types/models/trip-booking/trip-step'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformTripStepsFilterToUrl,
  transformUrlToTripStepsFilter
} from '../../../lib/utils/filters-mapping/trip-steps'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { calculateAffectedPlaces } from './utils'

const tripStepsFilter: TripStepTypes.Filter = {
  grouped: true
}

export function TripListPage() {
  const tableRef = useRef<DynamicTableRef<TripBookingTypes.Summary>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)

  const { modalState, toggleModal, closeModel, selectedData } = useModalManager<
    'filters' | 'view-mission' | 'delete-mission',
    number
  >(['filters', 'view-mission', 'delete-mission'])

  const {
    modalState: createModalState,
    toggleModal: createToggleModal,
    closeModel: createCloseModal,
    selectedData: createSelectedData
  } = useModalManager<'create-mission', TripBookingTypes.Summary[]>(['create-mission'])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<TripStepTypes.Filter>({
      initialFilters: tripStepsFilter,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformTripStepsFilterToUrl,
      transformUrlToFilter: transformUrlToTripStepsFilter
    })

  const { mutateAsync } = useMutation({
    mutationFn: createMission,
    onError: showAxiosError,
    onSuccess: () => {
      createCloseModal('create-mission')
      toast.success('Demande de mission créée avec succès')
      tableRef.current?.refetch()
    }
  })

  const { mutateAsync: cancelMissionMutate, isPending: isCancellingMission } = useMutation({
    mutationFn: cancelMission,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('delete-mission')
    }
  })

  const selectedDirectionality = useMemo(() => {
    if (!createSelectedData || createSelectedData.length === 0) return undefined
    const firstTripBooking = createSelectedData.at(0)
    return firstTripBooking?.type
  }, [createSelectedData])

  const columns: Column<TripBookingTypes.Summary>[] = [
    {
      accessorKey: 'type',
      header: 'Trajet',
      cell: ({ row }) => {
        const type = row.original.type
        return (
          <Badge
            text={tripStepTypeMapping[type].label}
            variant={tripStepTypeMapping[type].variant}
          />
        )
      }
    },
    {
      accessorKey: 'outboundTrip.relatedTrip.type',
      header: 'Type de voyage',
      cell: ({ row }) => {
        const tripBooking = row.original
        return tripBooking?.relatedTrip.type ? (
          <Badge
            variant={tripTypeMapping[tripBooking.relatedTrip.type].variant}
            text={tripTypeMapping[tripBooking.relatedTrip.type].label}
          />
        ) : (
          '-'
        )
      }
    },
    {
      accessorKey: 'departureDate',
      header: 'Date',
      cell: ({ row }) => <Badge text={DateFormatter.DDMMYYYY(row.original.date)} />
    },
    {
      accessorKey: 'timeBeforeDepartureForLEC',
      header: 'Heure de RDV LEC/AAT',
      cell: ({ row }) =>
        row.original.timeBeforeDeparture ? (
          <Badge
            text={valueRender(DateFormatter.getFormattedTime(row.original.timeBeforeDeparture))}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'departureTime',
      header: 'Heure de départ',
      cell: ({ row }) => (
        <Badge text={valueRender(DateFormatter.getFormattedTime(row.original.departureTime))} />
      )
    },
    {
      accessorKey: 'arrivalTime',
      header: "Heure d'arrivée",
      cell: ({ row }) => (
        <Badge text={valueRender(DateFormatter.getFormattedTime(row.original.arrivalTime))} />
      )
    },
    {
      accessorKey: 'outboundTrip.transportMeans.translations',
      header: 'Transport',
      cell: ({ row }) => {
        const picto = row.original.transportMeans?.picto
        if (!picto?.path) return getTranslatedName(row.original.transportMeans) || '-'

        return <TransportPicto picto={picto} />
      }
    },
    {
      accessorKey: 'departurePlace.city',
      header: 'Lieu de départ',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <CountryFlag possibleCountry={row.original.departurePlace?.country} />
          <span>{row.original.departurePlace?.label}</span>
        </div>
      )
    },
    {
      accessorKey: 'arrivalPlace.city',
      header: "Lieu d'arrivée",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <CountryFlag possibleCountry={row.original.arrivalPlace?.country} />
          <span>{row.original.arrivalPlace?.label}</span>
        </div>
      )
    },
    {
      accessorKey: 'outboundTrip.numberOfStudentsPlacesReserved',
      header: '# Réservés',
      cell: ({ row }) => {
        const tripBooking = row.original
        return `${tripBooking?.numberOfStudentsPlacesReserved ?? 0} + ${tripBooking?.numberOfStaffPlacesReserved ?? 0}`
      }
    },
    {
      accessorKey: 'outboundTrip.numberOfStaffPlacesReserved',
      header: '# Affectés',
      accessorFn: ({ missions, tripBookingDispatchPlaces }) =>
        calculateAffectedPlaces(missions, tripBookingDispatchPlaces)
    },
    {
      accessorKey: 'missions',
      header: 'Missions',
      cell: ({ row }) => (
        <div className="flex flex-col gap-1 py-2">
          {row.original.missions?.map((mission) => (
            <MissionRowDisplay
              key={mission.id}
              mission={mission}
              onIdClick={(id) => toggleModal('view-mission', id)}
            />
          ))}
        </div>
      )
    }
  ]

  return (
    <div>
      <UserNavBar title={'Liste des étapes'} icon={LocationThreeIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<TripBookingTypes.Summary, TripStepTypes.Filter>
        ref={tableRef}
        mode={'timeline'}
        title={'Toutes les étapes de toutes les réservations'}
        endpoint={API.tripSteps.getAll.url}
        columns={columns}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter, ['grouped'])}
        onFiltersClick={() => toggleModal('filters')}
        onLoadingChange={setIsTableLoading}
        selectable
        allowSearchParam
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            variant: 'primary',
            label: 'Créer une demande de mission',
            fn: (rows) => {
              const data = rows.map((row) => ('timeline' in row ? row.data : row)).flat()

              // Check if the data is within the same date and directionality
              const isValid = () => {
                if (!data || data.length === 0) return true
                const firstTripBooking = data.at(0)
                if (!firstTripBooking) return false
                return data.every(
                  (tripBooking) =>
                    isSameDay(tripBooking.date, firstTripBooking.date) &&
                    tripBooking.type === firstTripBooking.type
                )
              }

              if (!isValid())
                toast.error(
                  'Impossible de créer une mission pour des étapes de voyage différentes'
                )
              else createToggleModal('create-mission', data)
            }
          }
        ]}
        permissions={{ delete: false }}
      />

      <Modal
        isOpen={createModalState['create-mission']}
        title={'Créer une demande de mission'}
        icon={GoalIcon}
        size="medium"
        hideActions
        onCancel={() => createCloseModal('create-mission')}
      >
        <MissionForm
          defaultValues={{
            tripBookingIds: createSelectedData?.map((tripBooking) => tripBooking.tripBookingId)
          }}
          buttonsConfig={modalButtonConfig({
            onCancel: () => createCloseModal('create-mission'),
            onConfirm: async (values) => {
              await mutateAsync({ ...values, directionality: selectedDirectionality })
            }
          })}
        />
      </Modal>

      {selectedData && (
        <Modal
          isOpen={modalState['delete-mission']}
          title={`Vous êtes sur le point de supprimer cette mission`}
          size="medium"
          icon={DeleteIcon}
          variant={'destructive'}
          onCancel={() => {
            closeModel('delete-mission')
            toggleModal('view-mission', selectedData)
          }}
          actionButtonsOptions={{
            confirmText: 'Supprimer',
            cancelText: 'Fermer',
            onConfirm: async () => await cancelMissionMutate(selectedData)
          }}
        />
      )}

      <Modal
        isOpen={modalState['view-mission']}
        title={'Mission'}
        icon={Mission2Icon}
        size="large"
        hideActions
        onCancel={() => toggleModal('view-mission')}
      >
        {selectedData && (
          <MissionDetails
            missionId={selectedData}
            mode={'view'}
            showStaffTable
            onUpdated={() => tableRef.current?.refetch()}
          />
        )}

        <div className="flex justify-between items-center gap-2 p-6">
          <Button
            variant="destructive"
            loading={isCancellingMission}
            onClick={() => {
              if (!selectedData) return
              toggleModal('view-mission')
              toggleModal('delete-mission', selectedData)
            }}
            label="Annuler la mission"
          />
          <Button variant="primary" onClick={() => toggleModal('view-mission')} label="Valider" />
        </div>
      </Modal>

      <FilterDrawer<TripStepTypes.Filter>
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <TripStepsFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}
